import { useEffect } from 'react';
import { useNativePreferences } from '../storage/preferences';
import { isTelevisionRuntime } from '../utils/platformCapabilities';
import { performTVFeedback, unlockTVFeedbackAudio } from '../tvNavigation/feedback';
import type { FocusDirection } from '../tvNavigation/core';
import { emitTVAction } from '../tvNavigation/actions';
import { activateFocusedNode, backFocusedNode, getFocusRoute, getFocusTransitionCount, moveWebFocus, noteBack, setFocusModality, subscribeFocusTransitions } from '../tvNavigation/registry';

type RepeatState = { direction: FocusDirection | null; nextAt: number };

function directionFor(gamepad: Gamepad): FocusDirection | null {
    if (gamepad.buttons[12]?.pressed) return 'up';
    if (gamepad.buttons[13]?.pressed) return 'down';
    if (gamepad.buttons[14]?.pressed) return 'left';
    if (gamepad.buttons[15]?.pressed) return 'right';
    const horizontal = gamepad.axes[0] ?? 0;
    const vertical = gamepad.axes[1] ?? 0;
    if (Math.abs(horizontal) > Math.abs(vertical) && Math.abs(horizontal) >= 0.58) return horizontal < 0 ? 'left' : 'right';
    if (Math.abs(vertical) >= 0.58) return vertical < 0 ? 'up' : 'down';
    return null;
}

function pressedAction(gamepad: Gamepad): 'select' | 'back' | 'playPause' | 'seekBackward' | 'seekForward' | 'volumeDown' | 'volumeUp' | 'mute' | 'previousEpisode' | 'nextEpisode' | 'togglePlayerControls' | 'menu' | null {
    const buttons = gamepad.buttons;
    if (buttons[0]?.pressed) return 'select';
    if (buttons[1]?.pressed) return 'back';
    if (buttons[2]?.pressed) return 'previousEpisode';
    if (buttons[3]?.pressed) return 'nextEpisode';
    if (buttons[4]?.pressed) return 'seekBackward';
    if (buttons[5]?.pressed) return 'seekForward';
    if (buttons[6]?.pressed) return 'volumeDown';
    if (buttons[7]?.pressed) return 'volumeUp';
    if (buttons[8]?.pressed) return 'togglePlayerControls';
    if (buttons[9]?.pressed) return 'menu';
    if (buttons[10]?.pressed) return 'mute';
    if (buttons[11]?.pressed) return 'playPause';
    return null;
}

const keyboardDirections: Record<string, FocusDirection> = {
    ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
};

const keyboardActions: Record<string, Parameters<typeof emitTVAction>[0]['action']> = {
    MediaPlayPause: 'playPause', MediaTrackPrevious: 'previousEpisode', MediaTrackNext: 'nextEpisode',
    AudioVolumeUp: 'volumeUp', AudioVolumeDown: 'volumeDown', AudioVolumeMute: 'mute',
    BrowserBack: 'back', MediaRewind: 'seekBackward', MediaFastForward: 'seekForward',
};

const televisionKeyCodes: Record<number, Parameters<typeof emitTVAction>[0]['action']> = {
    10009: 'back', // Tizen Return
    415: 'playPause', 19: 'playPause', // Play / Pause
    412: 'seekBackward', 417: 'seekForward',
    447: 'volumeUp', 448: 'volumeDown', 449: 'mute',
};

function isTextControl(element: EventTarget | null | undefined) {
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement || (element instanceof HTMLElement && element.isContentEditable);
}

function isEditingText(event?: KeyboardEvent) {
    // React Native Web can deliver a key event from the real input before the
    // browser has updated document.activeElement. Prefer the event target so
    // TV navigation never consumes text-control keys during that window.
    return isTextControl(event?.target) || isTextControl(document.activeElement);
}

export function LivingRoomInput() {
    const preferences = useNativePreferences();

    useEffect(() => subscribeFocusTransitions((transition) => performTVFeedback({
        cue: transition.cue,
        modality: transition.modality,
        route: transition.route,
        settings: { uiSounds: preferences.uiSounds, controllerHaptics: preferences.controllerHaptics },
    })), [preferences.controllerHaptics, preferences.uiSounds]);

    useEffect(() => {
        // Try to start the feedback context as soon as the TV input adapter is
        // mounted. Browsers that permit autoplay are ready for the first
        // controller cue; browsers that require activation will still retry
        // from key/pointer input and expose their state in the debug overlay.
        unlockTVFeedbackAudio(false);
        let gamepadConnected = Array.from(navigator.getGamepads?.() ?? []).some((candidate) => candidate?.connected);
        let lastKeyboardDirectionAt = -Infinity;
        const handleConnection = () => { gamepadConnected = true; unlockTVFeedbackAudio(); };
        const handleDisconnection = () => { gamepadConnected = Array.from(navigator.getGamepads?.() ?? []).some((candidate) => candidate?.connected); };
        const handlePointerDown = () => unlockTVFeedbackAudio();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') unlockTVFeedbackAudio();
            const mediaAction = keyboardActions[event.key] ?? televisionKeyCodes[event.keyCode];
            if (mediaAction && !isEditingText(event)) {
                event.preventDefault();
                event.stopPropagation();
                setFocusModality('keyboard');
                if (mediaAction === 'back') {
                    if (!backFocusedNode()) noteBack();
                } else emitTVAction({ action: mediaAction, source: 'keyboard' });
                return;
            }
            const direction = keyboardDirections[event.key];
            const activate = event.key === 'Enter' || event.key === 'NumpadEnter' || event.key === ' ';
            // Web Arrow keys are the reference D-pad path. Consuming them
            // here prevents the browser/React Native Web focus model from
            // bypassing the semantic graph (notably from hero actions into
            // the sidebar). Text editing remains exempt below.
            if (direction && !isEditingText(event)) {
                event.preventDefault();
                event.stopPropagation();
                setFocusModality('keyboard');
                lastKeyboardDirectionAt = performance.now();
                const transitionsBefore = getFocusTransitionCount();
                moveWebFocus(direction);
                if (getFocusTransitionCount() === transitionsBefore) performTVFeedback({
                    cue: 'focus-item',
                    modality: 'keyboard',
                    route: getFocusRoute(),
                    settings: { uiSounds: preferences.uiSounds, controllerHaptics: preferences.controllerHaptics },
                });
            } else if (activate && (isTelevisionRuntime() || gamepadConnected) && !isEditingText(event)) {
                event.preventDefault();
                event.stopPropagation();
                setFocusModality('keyboard');
                activateFocusedNode();
            } else if (event.key === 'Escape' && (isTelevisionRuntime() || gamepadConnected)) {
                setFocusModality('keyboard');
                if (!backFocusedNode()) noteBack();
            }
        };
        const tizen = (window as Window & { tizen?: { tvinputdevice?: { registerKey?: (key: string) => void; unregisterKey?: (key: string) => void } } }).tizen;
        const tizenKeys = ['MediaPlayPause', 'MediaPlay', 'MediaPause', 'MediaRewind', 'MediaFastForward', 'VolumeUp', 'VolumeDown', 'VolumeMute'];
        tizenKeys.forEach((key) => { try { tizen?.tvinputdevice?.registerKey?.(key); } catch { /* unavailable outside Tizen */ } });
        window.addEventListener('gamepadconnected', handleConnection);
        window.addEventListener('gamepaddisconnected', handleDisconnection);
        window.addEventListener('pointerdown', handlePointerDown, true);
        window.addEventListener('touchstart', handlePointerDown, true);
        window.addEventListener('click', handlePointerDown, true);
        window.addEventListener('keydown', handleKeyDown, true);

        let frame = 0;
        let aPressed = false;
        let bPressed = false;
        const actionState = new Map<string, boolean>();
        const repeat: RepeatState = { direction: null, nextAt: 0 };
        const poll = (now: number) => {
            const gamepad = Array.from(navigator.getGamepads?.() ?? []).find((candidate): candidate is Gamepad => Boolean(candidate?.connected));
            if (gamepad && !document.hidden) {
                gamepadConnected = true;
                const direction = directionFor(gamepad);
                if (direction && now - lastKeyboardDirectionAt > 140 && (direction !== repeat.direction || now >= repeat.nextAt)) {
                    setFocusModality('gamepad');
                    moveWebFocus(direction);
                    repeat.nextAt = direction === repeat.direction ? now + 110 : now + 380;
                }
                repeat.direction = direction;
                const nextA = Boolean(gamepad.buttons[0]?.pressed);
                if (nextA && !aPressed) {
                    setFocusModality('gamepad');
                    activateFocusedNode();
                }
                aPressed = nextA;
                const nextB = Boolean(gamepad.buttons[1]?.pressed);
                if (nextB && !bPressed) {
                    setFocusModality('gamepad');
                    const handled = backFocusedNode();
                    if (!handled) {
                        noteBack();
                        const activeDialog = document.querySelector('[data-testid="tv-focus-dialog"], [role="dialog"][aria-modal="true"], [aria-modal="true"]');
                        if (activeDialog) activeDialog.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
                        else if (window.location.pathname !== '/' && window.location.pathname !== '/login') window.history.back();
                    }
                }
                bPressed = nextB;
                const action = pressedAction(gamepad);
                if (action && action !== 'select' && action !== 'back' && !actionState.get(action)) {
                    setFocusModality('gamepad');
                    emitTVAction({ action, source: 'gamepad' });
                }
                for (const candidate of ['playPause', 'seekBackward', 'seekForward', 'volumeDown', 'volumeUp', 'mute', 'previousEpisode', 'nextEpisode', 'togglePlayerControls', 'menu']) {
                    actionState.set(candidate, Boolean(pressedAction(gamepad) === candidate));
                }
            } else {
                repeat.direction = null;
                aPressed = false;
                bPressed = false;
                actionState.clear();
            }
            frame = requestAnimationFrame(poll);
        };
        frame = requestAnimationFrame(poll);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('gamepadconnected', handleConnection);
            window.removeEventListener('gamepaddisconnected', handleDisconnection);
            window.removeEventListener('pointerdown', handlePointerDown, true);
            window.removeEventListener('touchstart', handlePointerDown, true);
            window.removeEventListener('click', handlePointerDown, true);
            window.removeEventListener('keydown', handleKeyDown, true);
            tizenKeys.forEach((key) => { try { tizen?.tvinputdevice?.unregisterKey?.(key); } catch { /* unavailable outside Tizen */ } });
        };
    }, []);
    return null;
}
